import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Save } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import type { AppRecord, SaveAppInput } from "@/api/types";
import { useAppsQuery, useSaveAppMutation } from "@/api/query-hooks";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { isMailArchiverApp, MailArchiverIcon } from "@/components/mailarchiver-icon";
import { formatDateTime } from "@/lib/date";
import { ReleasesPanel } from "@/features/apps/releases-panel";

const appSchema = z.object({
  id: z.string().min(2).max(64).regex(/^[a-z0-9][a-z0-9_-]*$/),
  name: z.string().min(2).max(120),
  defaultThresholdDays: z.coerce.number().int().min(1).max(365),
  defaultWarningDays: z.coerce.number().int().min(1).max(120),
  defaultOfflineGraceDays: z.coerce.number().int().min(1).max(120),
  defaultReadonlyGraceDays: z.coerce.number().int().min(1).max(120),
  minSupportedVersion: z.string().trim().max(64).refine((value) => value === "" || /^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/.test(value), {
    message: "Expected semver, for example 1.4.0"
  }),
  certSigningKeyVersion: z.coerce.number().int().min(1).max(1000)
});

type AppFormValues = z.infer<typeof appSchema>;

export function AppsPage() {
  const { t, i18n } = useTranslation(["apps", "common"]);
  const query = useAppsQuery();
  const [editing, setEditing] = useState<AppRecord | null>(null);
  const [open, setOpen] = useState(false);

  if (query.isLoading) {
    return <LoadingState />;
  }
  if (query.isError) {
    return <ErrorState title={t("common:states.error")} detail={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const apps = query.data?.items ?? [];

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t("title")}</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("description")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(null)}>
              <Plus className="h-4 w-4" />
              {t("create")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? t("edit") : t("create")}</DialogTitle>
              <DialogDescription>{t("description")}</DialogDescription>
            </DialogHeader>
            <AppForm
              app={editing}
              onSaved={() => {
                setOpen(false);
                setEditing(null);
                toast.success(t("saved"));
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {apps.length === 0 ? (
        <EmptyState title={t("common:states.empty")} action={<Button onClick={() => setOpen(true)}>{t("create")}</Button>} />
      ) : (
        <>
          <section className="overflow-hidden rounded-md border bg-panel">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] border-collapse text-sm">
                <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">{t("id")}</th>
                    <th className="px-3 py-2">{t("name")}</th>
                    <th className="px-3 py-2">{t("thresholdDays")}</th>
                    <th className="px-3 py-2">{t("warningDays")}</th>
                    <th className="px-3 py-2">{t("minSupportedVersion")}</th>
                    <th className="px-3 py-2">Updated</th>
                    <th className="px-3 py-2 text-right">{t("common:actions.open")}</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((app) => (
                    <tr key={app.id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">
                        <span className="flex items-center gap-2">
                          {isMailArchiverApp(app.id) && <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />}
                          {app.id}
                        </span>
                      </td>
                      <td className="px-3 py-2">{app.name}</td>
                      <td className="px-3 py-2">{app.defaultThresholdDays}</td>
                      <td className="px-3 py-2">{app.defaultWarningDays}</td>
                      <td className="px-3 py-2">{app.minSupportedVersion || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{formatDateTime(app.updatedAt, i18n.language)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditing(app);
                            setOpen(true);
                          }}
                        >
                          {t("edit")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <ReleasesPanel apps={apps} />
        </>
      )}
    </div>
  );
}

function AppForm({ app, onSaved }: { app: AppRecord | null; onSaved: () => void }) {
  const { t } = useTranslation(["apps", "common"]);
  const mutation = useSaveAppMutation();
  const form = useForm<AppFormValues>({
    resolver: zodResolver(appSchema),
    defaultValues: {
      id: app?.id ?? "mailarchiver",
      name: app?.name ?? "MailArchiver",
      defaultThresholdDays: app?.defaultThresholdDays ?? 30,
      defaultWarningDays: app?.defaultWarningDays ?? 7,
      defaultOfflineGraceDays: app?.defaultOfflineGraceDays ?? 7,
      defaultReadonlyGraceDays: app?.defaultReadonlyGraceDays ?? 7,
      minSupportedVersion: app?.minSupportedVersion ?? "",
      certSigningKeyVersion: app?.certSigningKeyVersion ?? 1
    }
  });

  const submit = form.handleSubmit((values) => {
    const input: SaveAppInput = {
      ...values,
      minSupportedVersion: values.minSupportedVersion || undefined
    };
    mutation.mutate(input, {
      onSuccess: onSaved,
      onError: (error) => {
        if (error instanceof ApiError && error.status === 409) {
          form.setError("id", { type: "server", message: t("duplicate") });
          toast.error(t("duplicate"));
          return;
        }
        toast.error(error instanceof Error ? error.message : t("common:states.error"));
      }
    });
  });

  return (
    <form className="grid gap-3" onSubmit={submit}>
      <Field label={t("id")} error={form.formState.errors.id?.message}>
        <Input {...form.register("id")} disabled={Boolean(app)} />
      </Field>
      <Field label={t("name")} error={form.formState.errors.name?.message}>
        <Input {...form.register("name")} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("thresholdDays")} error={form.formState.errors.defaultThresholdDays?.message}>
          <Input type="number" {...form.register("defaultThresholdDays")} />
        </Field>
        <Field label={t("warningDays")} error={form.formState.errors.defaultWarningDays?.message}>
          <Input type="number" {...form.register("defaultWarningDays")} />
        </Field>
        <Field label={t("offlineGraceDays")} error={form.formState.errors.defaultOfflineGraceDays?.message}>
          <Input type="number" {...form.register("defaultOfflineGraceDays")} />
        </Field>
        <Field label={t("readonlyGraceDays")} error={form.formState.errors.defaultReadonlyGraceDays?.message}>
          <Input type="number" {...form.register("defaultReadonlyGraceDays")} />
        </Field>
      </div>
      <Field label={t("minSupportedVersion")} error={form.formState.errors.minSupportedVersion?.message}>
        <Input {...form.register("minSupportedVersion")} />
      </Field>
      <Field label={t("certSigningKeyVersion")} error={form.formState.errors.certSigningKeyVersion?.message}>
        <Input type="number" {...form.register("certSigningKeyVersion")} />
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" disabled={mutation.isPending}>
          <Save className="h-4 w-4" />
          {t("common:actions.save")}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
      {error ? <div className="text-xs text-destructive">{error}</div> : null}
    </div>
  );
}
